import React, { useState, useEffect } from 'react';
import './TutorialModal.css';

const TutorialModal = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const tutorialSteps = [
    {
      title: "Welcome to Ideal Scribe!",
      description: "Let's get you started with a quick tour of how to organize your ideas and plan amazing trips.",
      content: (
        <div className="tutorial-content">
          <div className="tutorial-icon">✨</div>
          <p className="tutorial-text">
            Ideal Scribe helps you capture notes, discover journeys, and create personalized travel plans called Pack-Its.
          </p>
          <div className="tutorial-features">
            <div className="feature-item">
              <i className="fas fa-sticky-note"></i>
              <span>Capture Quick Notes</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marked-alt"></i>
              <span>Discover Journeys</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-suitcase-rolling"></i>
              <span>Create Trip Pack-Its</span>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Complete Your Account Settings",
      description: "Personalize your Ideal Scribe experience by setting up your account preferences.",
      content: (
        <div className="tutorial-content">
          <div className="tutorial-icon">⚙️</div>
          <p className="tutorial-text">
            Setting up your account helps Ideal Scribe provide personalized recommendations and better organize your content:
          </p>
          <div className="tutorial-steps-list">
            <div className="step-item">
              <div className="step-number">1</div>
              <div className="step-details">
                <strong>Go to Account Settings</strong>
                <p>Click "Account Settings" in the navigation menu at the top</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">2</div>
              <div className="step-details">
                <strong>Set your starting location</strong>
                <p>Enter your home base so we can calculate distances and suggest nearby journeys</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">3</div>
              <div className="step-details">
                <strong>Customize your preferences</strong>
                <p>Set your personalized preferences, customize color categories, and update your profile</p>
              </div>
            </div>
          </div>
          <div className="tutorial-tip">
            <i className="fas fa-lightbulb"></i>
            <span>Tip: Your location helps us suggest journeys and calculate travel distances for your Pack-Its!</span>
          </div>
        </div>
      )
    },
    {
      title: "Step 1: Create a Note",
      description: "Start by capturing your ideas, thoughts, or travel inspirations as notes.",
      content: (
        <div className="tutorial-content">
          <div className="tutorial-icon">📝</div>
          <p className="tutorial-text">
            Notes are the foundation of your Ideal Scribe experience. You can create notes in several ways:
          </p>
          <div className="tutorial-steps-list">
            <div className="step-item">
              <div className="step-number">1</div>
              <div className="step-details">
                <strong>Click "New Note" button</strong>
                <p>Located in the top right of your Quick Notes page</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">2</div>
              <div className="step-details">
                <strong>Add your content</strong>
                <p>Write your note, add tags, categories, and organize it</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">3</div>
              <div className="step-details">
                <strong>Email notes on the go</strong>
                <p>Send emails to <strong>scribe@quicknotes.idealscribe.com</strong> to create notes instantly</p>
              </div>
            </div>
          </div>
          <div className="tutorial-tip">
            <i className="fas fa-lightbulb"></i>
            <span>Tip: Notes can be converted into Pack-Its later to plan your trips!</span>
          </div>
        </div>
      )
    },
    {
      title: "Step 2: Find a Journey",
      description: "Discover amazing travel journeys created by our community or the Ideal Scribe system.",
      content: (
        <div className="tutorial-content">
          <div className="tutorial-icon">🗺️</div>
          <p className="tutorial-text">
            Journeys are curated travel routes that you can explore and add to your collection:
          </p>
          <div className="tutorial-steps-list">
            <div className="step-item">
              <div className="step-number">1</div>
              <div className="step-details">
                <strong>Switch to "My Explorer Board"</strong>
                <p>Click the tab at the top to view available journeys</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">2</div>
              <div className="step-details">
                <strong>Browse or search journeys</strong>
                <p>Use filters to find journeys that match your interests</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">3</div>
              <div className="step-details">
                <strong>Add journeys to your board</strong>
                <p>Click "Add Journey" to save journeys you want to explore</p>
              </div>
            </div>
          </div>
          <div className="tutorial-tip">
            <i className="fas fa-lightbulb"></i>
            <span>Tip: Journeys show on the map so you can visualize your travel plans!</span>
          </div>
        </div>
      )
    },
    {
      title: "Step 3: Create a Pack-It",
      description: "Transform your notes and journeys into personalized travel plans called Pack-Its.",
      content: (
        <div className="tutorial-content">
          <div className="tutorial-icon">🎒</div>
          <p className="tutorial-text">
            Pack-Its are your personalized travel plans that combine notes, journeys, and your preferences:
          </p>
          <div className="tutorial-steps-list">
            <div className="step-item">
              <div className="step-number">1</div>
              <div className="step-details">
                <strong>Go to Pack-It page</strong>
                <p>Click "Trip Pack-it" in the navigation or use the "Convert to Pack-It" button</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">2</div>
              <div className="step-details">
                <strong>Choose creation method</strong>
                <p>Select from Quick Trip, Custom Trip, Trip Mood, or Roll the Dice</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">3</div>
              <div className="step-details">
                <strong>Add your trip details</strong>
                <p>Select options and tailor your trip to build your dream vacation in your Pack-It</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">4</div>
              <div className="step-details">
                <strong>Plan and explore!</strong>
                <p>Your Pack-It will help organize your trip with destinations, activities, and more</p>
              </div>
            </div>
          </div>
          <div className="tutorial-tip">
            <i className="fas fa-lightbulb"></i>
            <span>Tip: Pack-Its can include multiple destinations, dates, and activities from your notes!</span>
          </div>
        </div>
      )
    },
    {
      title: "You're All Set!",
      description: "Start organizing your ideas and planning your next adventure.",
      content: (
        <div className="tutorial-content">
          <div className="tutorial-icon">🚀</div>
          <p className="tutorial-text">
            You now know the basics of Ideal Scribe! Here's your action plan:
          </p>
          <div className="tutorial-action-plan">
            <div className="action-item">
              <i className="fas fa-check-circle"></i>
              <span>Create your first note</span>
            </div>
            <div className="action-item">
              <i className="fas fa-check-circle"></i>
              <span>Explore available journeys</span>
            </div>
            <div className="action-item">
              <i className="fas fa-check-circle"></i>
              <span>Build your first Pack-It</span>
            </div>
          </div>
          <div className="tutorial-help">
            <p>Need help? Check out the <strong>Startup Guide</strong> in the navigation for detailed instructions.</p>
          </div>
          <div className="tutorial-reminder">
            <i className="fas fa-info-circle"></i>
            <span>Remember: AI-generated information should be verified. Always contact providers directly for the latest information.</span>
          </div>
        </div>
      )
    }
  ];

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete();
    }
    onClose();
  };

  const handleSkip = () => {
    onClose();
  };

  if (!isOpen) return null;

  const step = tutorialSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tutorialSteps.length - 1;

  return (
    <div className="tutorial-modal-overlay" onClick={handleSkip}>
      <div className="tutorial-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tutorial-modal-header">
          <div className="tutorial-progress">
            {tutorialSteps.map((_, index) => (
              <div
                key={index}
                className={`progress-dot ${index <= currentStep ? 'active' : ''}`}
              />
            ))}
          </div>
          <button className="tutorial-close-btn" onClick={handleSkip}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="tutorial-modal-body">
          <h2 className="tutorial-title">{step.title}</h2>
          <p className="tutorial-description">{step.description}</p>
          <div className="tutorial-content-wrapper">
            {step.content}
          </div>
        </div>

        <div className="tutorial-modal-footer">
          <button className="tutorial-btn tutorial-btn-secondary" onClick={handleSkip}>
            Skip Tutorial
          </button>
          <div className="tutorial-nav-buttons">
            {!isFirstStep && (
              <button className="tutorial-btn tutorial-btn-secondary" onClick={handlePrevious}>
                <i className="fas fa-chevron-left"></i>
                Previous
              </button>
            )}
            <button className="tutorial-btn tutorial-btn-primary" onClick={handleNext}>
              {isLastStep ? 'Get Started!' : 'Next'}
              {!isLastStep && <i className="fas fa-chevron-right"></i>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialModal;
